import { useQuery } from "@tanstack/react-query";
import { useRecords } from "../../../api/useRecords";
import { getItems } from "../../../api/items";
import { DASHBOARD_COLORS, GAUGE_BAND_COLORS } from "../../../dashboard-framework/colors";
import { formatDateRangePt } from "../../../dashboard-framework/describeWidgetLogic";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { GlobalFilterConfig } from "../../../dashboard-framework/types";
import { useGlobalFilterValues } from "../../../dashboard-framework/useGlobalFilterValues";
import { formatEuros } from "../../../dashboard-framework/format";
import { PivotNode } from "../../../dashboard-framework/pivot";
import { applyServiceSeriesColors, useServiceColors } from "../../../dashboard-framework/serviceColors";
import { DonutCard } from "../../../dashboard-framework/widgets/DonutCard";
import { PivotTableCard } from "../../../dashboard-framework/widgets/PivotTableCard";
import { formatDrillDate } from "../shared/drillColumns";
import { StackedBarChart } from "../../../dashboard-framework/widgets/StackedBarChart";
import { TimeSeriesChart } from "../../../dashboard-framework/widgets/TimeSeriesChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { Grid } from "@mui/material";
import {
    avgPriceEvolutionPoints,
    computePriceItems,
    priceAnalysisPivot,
    PriceComputedItem,
    slicesFromSums,
    stackedPointsByService,
    totalsByAgreement,
    totalsByGroupedDisorder,
    totalsByVariantType,
} from "./pricesRows";

const SLICE_COLORS = [
    DASHBOARD_COLORS.highlight,
    "#2EBD6B",
    "#E25555",
    "#F08C3C",
    "#7C5CD6",
    "#18B7D9",
    "#7C8492",
    GAUGE_BAND_COLORS.middle,
];
const colorForIndex = (index: number) => SLICE_COLORS[index % SLICE_COLORS.length];
const euroFormatter = formatEuros;

const pricesGlobalFilters: Array<GlobalFilterConfig> = [
    { key: "timeline", type: "datePreset", label: "Período (data da encomenda)", field: "order_date", defaultPreset: "este_ano" },
];

const SEM_ACORDO_OPTION_VALUE = "__sem_acordo__";

export const PricesDashboard = () => {
    const { values, setValue } = useGlobalFilterValues(pricesGlobalFilters);

    const now = new Date();
    const timelineRange = resolveRequiredDateFilterRange(values, "timeline", "este_ano", now);

    const orderItemsResult = useQuery({
        queryKey: ["prices-order-items", timelineRange.gte, timelineRange.lt],
        queryFn: () =>
            getItems(
                "order_items",
                "id,price,quantity,discount,order_id.id,order_id.order_date,order_id.treatment.diagnose_main,order_id.treatment.client.client_id,service_variant.type,service_variant.service,service_variant.agreement",
                {
                    filter: { order_id: { order_date: { _gte: timelineRange.gte, _lt: timelineRange.lt } } },
                    pageSize: 8000,
                    sort: "id",
                }
            ),
        staleTime: 5 * 60 * 1000,
    });
    const servicesResult = useRecords("services", "id,name");
    const agreementsResult = useRecords("agreements", "id,name");
    const { serviceColorById } = useServiceColors();

    const serviceNamesById: Record<number, string> = Object.fromEntries(
        (servicesResult.data?.items || []).map((service: { id: number; name: string }) => [service.id, service.name])
    );
    const agreementNamesById: Record<number, string> = Object.fromEntries(
        (agreementsResult.data?.items || []).map((agreement: { id: number; name: string }) => [agreement.id, agreement.name])
    );

    const allItems = computePriceItems(orderItemsResult.data?.items || []);

    // Réplica dos filtros globais Zoho Serviço/Acordo/Tipo: opções derivadas dos dados, aplicadas a todos os widgets.
    const serviceOptions = [...new Set(allItems.map((item) => item.serviceId).filter((serviceId) => serviceId !== null))]
        .map((serviceId) => ({ value: serviceId as number, label: serviceNamesById[serviceId as number] || `Serviço #${serviceId}` }))
        .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label));
    const agreementOptions = [
        { value: SEM_ACORDO_OPTION_VALUE, label: "Sem Acordo" },
        ...[...new Set(allItems.map((item) => item.agreementId).filter((agreementId) => agreementId !== null))]
            .map((agreementId) => ({ value: agreementId as number, label: agreementNamesById[agreementId as number] || `Acordo #${agreementId}` }))
            .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label)),
    ];
    const typeOptions = [...new Set(allItems.map((item) => item.variantType))]
        .sort()
        .map((variantType) => ({ value: variantType, label: variantType }));

    const dynamicGlobalFilters: Array<GlobalFilterConfig> = [
        pricesGlobalFilters[0],
        { key: "service", type: "select", label: "Serviço", field: "service", options: serviceOptions },
        { key: "agreement", type: "select", label: "Acordo", field: "agreement", options: agreementOptions },
        { key: "variantType", type: "select", label: "Tipo", field: "type", options: typeOptions },
    ];

    const selectedService = values.service ?? null;
    const selectedAgreement = values.agreement ?? null;
    const selectedType = values.variantType ?? null;
    const computedItems = allItems.filter((item) => {
        const matchesService = selectedService === null || item.serviceId === Number(selectedService);
        const matchesAgreement =
            selectedAgreement === null ||
            (selectedAgreement === SEM_ACORDO_OPTION_VALUE ? item.agreementId === null : item.agreementId === Number(selectedAgreement));
        const matchesType = selectedType === null || item.variantType === selectedType;
        return matchesService && matchesAgreement && matchesType;
    });

    const typeSlices = slicesFromSums(totalsByVariantType(computedItems), colorForIndex);
    const agreementTotals = totalsByAgreement(computedItems, agreementNamesById);
    const agreementEurosSlices = slicesFromSums(agreementTotals.euros, colorForIndex);
    const agreementQuantitySlices = slicesFromSums(agreementTotals.quantities, colorForIndex);
    const disorderSlices = slicesFromSums(totalsByGroupedDisorder(computedItems), colorForIndex);

    const byAgreementStack = stackedPointsByService(computedItems, serviceNamesById, (item) =>
        item.agreementId === null ? "Sem Acordo" : agreementNamesById[item.agreementId] || `Acordo #${item.agreementId}`
    );
    const byTypeStack = stackedPointsByService(computedItems, serviceNamesById, (item) => item.variantType);

    const analysisPivot = priceAnalysisPivot(computedItems, serviceNamesById);
    const evolution = avgPriceEvolutionPoints(computedItems, serviceNamesById);
    const evolutionSeries = applyServiceSeriesColors(
        evolution.services.map((service, index) => ({ key: service.id, label: service.name, color: colorForIndex(index) })),
        serviceColorById
    );

    const isLoading = orderItemsResult.isLoading;
    const isError = orderItemsResult.isError;

    const periodText = `Considera itens de encomendas com data ${formatDateRangePt(timelineRange)}, respeitando os filtros globais de Serviço, Acordo e Tipo quando selecionados.`;
    const itemTotalText =
        "Cada item vale quantidade × preço, com desconto percentual aplicado quando existe.";

    const explanations = {
        analysis: {
            queryDescription: `Vendas por serviço e tipo de variante: total €, preço médio (total ÷ quantidade), quantidade e % do total. ${periodText} ${itemTotalText} Exclui encomendas de clientes internos (número de cliente começado por "I").`,
        },
        evolution: {
            queryDescription: `Preço unitário médio dos itens de encomenda, por serviço e por mês da data da encomenda. ${periodText}`,
        },
        typeDonut: {
            queryDescription: `Soma do valor dos itens por tipo de variante do serviço (Mensal, Individual, combos; sem tipo definido aparece como "Sem tipo"). ${periodText} ${itemTotalText}`,
        },
        agreementEuros: {
            queryDescription: `Soma do valor dos itens por acordo da variante do serviço; variantes sem acordo contam como "Sem Acordo". ${periodText} ${itemTotalText}`,
        },
        agreementQuantity: {
            queryDescription: `Soma das quantidades dos itens por acordo da variante do serviço; variantes sem acordo contam como "Sem Acordo". ${periodText}`,
        },
        serviceByAgreement: {
            queryDescription: `Valor total dos itens por serviço, empilhado por acordo. ${periodText} ${itemTotalText}`,
        },
        serviceByType: {
            queryDescription: `Valor total dos itens por serviço, empilhado por tipo de variante. ${periodText} ${itemTotalText}`,
        },
        disorders: {
            queryDescription: `Soma do valor dos itens por grupo de transtorno do tratamento da encomenda (transtorno principal agrupado em: PHDA, Autismo, Ansiedade, Outros; sem transtorno conta como Outros). ${periodText} ${itemTotalText}`,
        },
    };

    const analysisCellDrillDown = (node: PivotNode<PriceComputedItem>, columnKey: string) => {
        const measureLabel = analysisPivot.columns.find((column) => column.key === columnKey)?.label || columnKey;
        const drill = {
            kind: "rows" as const,
            title: `${node.path.join(" › ")} — ${measureLabel}`,
            queryDescription:
                "Itens de encomenda que compõem a célula selecionada (cada linha é um item, não uma encomenda). Clica numa linha para abrir a encomenda.",
            columns: [
                { key: "orderDate", label: "Data da encomenda", valueFor: (item: PriceComputedItem) => formatDrillDate(item.orderDate) },
                { key: "orderId", label: "Encomenda" },
                { key: "variantType", label: "Tipo" },
                { key: "quantity", label: "Quantidade", isNumeric: true },
                { key: "total", label: "Total", isNumeric: true, valueFor: (item: PriceComputedItem) => formatEuros(item.total) },
            ],
            rows: node.items,
            defaultSortKey: "orderDate",
            rowRoute: (item: PriceComputedItem) => (item.orderId ? `/orders/${item.orderId}` : null),
        };
        return drill;
    };

    return (
        <DashboardPage globalFilters={dynamicGlobalFilters} values={values} onChange={setValue}>
            <PivotTableCard
                title="Análise de preços (sem pessoas internas)"
                explanation={explanations.analysis}
                pivot={analysisPivot}
                rowHeaderLabel="Serviço / Tipo"
                maxBodyHeight={460}
                isLoading={isLoading}
                isError={isError}
                cellDrillDown={analysisCellDrillDown}
            />

            <WidgetCard title="Evolução de preços" explanation={explanations.evolution} bodyHeight={360} isLoading={isLoading} isError={isError}>
                <TimeSeriesChart points={evolution.points} series={evolutionSeries} height={360} showPointLabels={false} />
            </WidgetCard>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <WidgetCard
                        title="Preço por serviço por tipo"
                        explanation={explanations.serviceByType}
                        bodyHeight={360}
                        isLoading={isLoading}
                        isError={isError}
                    >
                        <StackedBarChart
                            points={byTypeStack.points}
                            series={byTypeStack.stackLabels.map((label, index) => ({ key: label, label, color: colorForIndex(index) }))}
                            categoryKey="serviceName"
                            valueFormatter={euroFormatter}
                            showBarLabels
                            showTotalLabel
                        />
                    </WidgetCard>
                </Grid>
                <Grid item xs={12} md={6}>
                    <DonutCard
                        title="Preço por tipo"
                        explanation={explanations.typeDonut}
                        slices={typeSlices}
                        valueFormatter={euroFormatter}
                        isLoading={isLoading}
                        isError={isError}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <DonutCard
                        title="Quantidade por acordo"
                        explanation={explanations.agreementQuantity}
                        slices={agreementQuantitySlices}
                        isLoading={isLoading}
                        isError={isError}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <DonutCard
                        title="Valor por acordo"
                        explanation={explanations.agreementEuros}
                        slices={agreementEurosSlices}
                        valueFormatter={euroFormatter}
                        isLoading={isLoading}
                        isError={isError}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <WidgetCard
                        title="Preço por serviço por acordo"
                        explanation={explanations.serviceByAgreement}
                        bodyHeight={360}
                        isLoading={isLoading}
                        isError={isError}
                    >
                        <StackedBarChart
                            points={byAgreementStack.points}
                            series={byAgreementStack.stackLabels.map((label, index) => ({ key: label, label, color: colorForIndex(index) }))}
                            categoryKey="serviceName"
                            valueFormatter={euroFormatter}
                            showBarLabels
                            showTotalLabel
                        />
                    </WidgetCard>
                </Grid>
                <Grid item xs={12} md={6}>
                    <DonutCard
                        title="Vendas por grupo de transtorno"
                        explanation={explanations.disorders}
                        slices={disorderSlices}
                        valueFormatter={euroFormatter}
                        isLoading={isLoading}
                        isError={isError}
                    />
                </Grid>
            </Grid>
        </DashboardPage>
    );
};
